import { getAttachmentFileType } from './attachmentTypeUtils';

// ─── Icon base attributes (Lucide-style, matching Icons.jsx convention) ───────
const BASE = {
  xmlns: 'http://www.w3.org/2000/svg',
  width: '24',
  height: '24',
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: '1.8',
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

// ─── Individual icon shapes ───────────────────────────────────────────────────

const ImageIcon = (props) => (
  <svg {...BASE} {...props}>
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <polyline points="21 15 16 10 5 21" />
  </svg>
);

const PdfIcon = (props) => (
  <svg {...BASE} {...props}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14   2 14 8 20 8" />
    <path d="M9 13h2a1.5 1.5 0 0 0 0-3H9v6" />
    <line x1="15" y1="10" x2="15" y2="16" />
    <line x1="13" y1="13" x2="17" y2="13" />
  </svg>
);

const WordIcon = (props) => (
  <svg {...BASE} {...props}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="8" y1="13" x2="16" y2="13" />
    <line x1="8" y1="17" x2="13" y2="17" />
  </svg>
);

const ExcelIcon = (props) => (
  <svg {...BASE} {...props}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="8" y1="12" x2="16" y2="12" />
    <line x1="8" y1="16" x2="16" y2="16" />
    <line x1="12" y1="12" x2="12" y2="20" />
  </svg>
);

const CsvIcon = (props) => (
  <svg {...BASE} {...props}>
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <line x1="3" y1="9" x2="21" y2="9" />
    <line x1="3" y1="15" x2="21" y2="15" />
    <line x1="9" y1="9" x2="9" y2="21" />
  </svg>
);

const PowerPointIcon = (props) => (
  <svg {...BASE} {...props}>
    <rect x="2" y="3" width="20" height="14" rx="2" />
    <line x1="8" y1="21" x2="16" y2="21" />
    <line x1="12" y1="17" x2="12" y2="21" />
  </svg>
);

const TextIcon = (props) => (
  <svg {...BASE} {...props}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="8" y1="13" x2="16" y2="13" />
    <line x1="8" y1="17" x2="16" y2="17" />
    <line x1="8" y1="9" x2="10" y2="9" />
  </svg>
);

const ArchiveIcon = (props) => (
  <svg {...BASE} {...props}>
    <polyline points="21 8 21 21 3 21 3 8" />
    <rect x="1" y="3" width="22" height="5" />
    <line x1="10" y1="12" x2="14" y2="12" />
  </svg>
);

const VideoIcon = (props) => (
  <svg {...BASE} {...props}>
    <polygon points="23 7 16 12 23 17 23 7" />
    <rect x="1" y="5" width="15" height="14" rx="2" />
  </svg>
);

const AudioIcon = (props) => (
  <svg {...BASE} {...props}>
    <path d="M9 18V5l12-2v13" />
    <circle cx="6" cy="18" r="3" />
    <circle cx="18" cy="16" r="3" />
  </svg>
);

const LinkIcon = (props) => (
  <svg {...BASE} {...props}>
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

const UnknownIcon = (props) => (
  <svg {...BASE} {...props}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

const ICON_MAP = {
  image: ImageIcon,
  pdf: PdfIcon,
  word: WordIcon,
  excel: ExcelIcon,
  csv: CsvIcon,
  powerpoint: PowerPointIcon,
  text: TextIcon,
  archive: ArchiveIcon,
  video: VideoIcon,
  audio: AudioIcon,
  link: LinkIcon,
  unknown: UnknownIcon,
};

// ─── Main exported component ──────────────────────────────────────────────────

/**
 * Renders a themed icon container for an attachment file.
 * Resolves the icon from MIME type (primary) or file extension (fallback).
 *
 * @param {object}  props
 * @param {string}  [props.mimeType]   - MIME type of the attachment
 * @param {string}  [props.fileName]   - File name (extension fallback)
 * @param {string}  [props.className]  - Additional classes on the container
 */
export function AttachmentTypeIcon({ mimeType, fileName, className = '' }) {
  const { type, label } = getAttachmentFileType(mimeType, fileName);
  const Icon = ICON_MAP[type] || UnknownIcon;

  let labelText = type === 'unknown' ? 'FILE' : type.toUpperCase();
  if (type === 'image' && fileName) {
    const ext = fileName.split('.').pop();
    if (ext) {
      labelText = ext.toUpperCase();
    }
  }

  return (
    <div
      className={`w-14 h-14 shrink-0 rounded-[8px] bg-surface-mint text-primary flex flex-col items-center justify-center gap-0.5 ${className}`}
      title={label}
      aria-label={label}
      role="img"
    >
      <Icon className="w-5 h-5" />
      <span className="text-[9px] font-semibold text-primary leading-none tracking-wide uppercase opacity-70 select-none">
        {labelText}
      </span>
    </div>
  );
}
